import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useScroll } from "../../scroll/ScrollProvider";

const NODES = 96;
const CONNECTIONS_PER_NODE = 2;
const RADIUS = 6;

/**
 * Constellation — abstract network of glowing nodes connected by thin lines.
 * Drifts slowly. As the user scrolls into section 3, more connections fade in
 * and the whole graph rotates a touch.
 */
export function Constellation() {
  const group = useRef<THREE.Group>(null);
  const points = useRef<THREE.Points>(null);
  const lines = useRef<THREE.LineSegments>(null);
  const { section, sectionProgress } = useScroll();

  const { positions, lineGeom } = useMemo(() => {
    const positions = new Float32Array(NODES * 3);
    for (let i = 0; i < NODES; i++) {
      // Sphere distribution biased to a flatter disc
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = RADIUS * (0.5 + Math.random() * 0.5);
      positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.5;
      positions[i * 3 + 2] = r * Math.cos(phi);
    }

    const lineSegs: number[] = [];
    for (let i = 0; i < NODES; i++) {
      const ax = positions[i * 3 + 0];
      const ay = positions[i * 3 + 1];
      const az = positions[i * 3 + 2];
      // Find nearest neighbours
      const dists: { j: number; d: number }[] = [];
      for (let j = 0; j < NODES; j++) {
        if (i === j) continue;
        const dx = positions[j * 3 + 0] - ax;
        const dy = positions[j * 3 + 1] - ay;
        const dz = positions[j * 3 + 2] - az;
        dists.push({ j, d: dx * dx + dy * dy + dz * dz });
      }
      dists.sort((a, b) => a.d - b.d);
      for (let k = 0; k < CONNECTIONS_PER_NODE; k++) {
        const j = dists[k].j;
        if (j > i) {
          lineSegs.push(ax, ay, az, positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2]);
        }
      }
    }
    const lineGeom = new THREE.BufferGeometry();
    lineGeom.setAttribute("position", new THREE.Float32BufferAttribute(lineSegs, 3));
    return { positions, lineGeom };
  }, []);

  const pointsGeom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    return g;
  }, [positions]);

  useFrame((_state, delta) => {
    const active = section < 10 ? Math.max(0, (section - 8) / 2) : section === 10 ? sectionProgress : 1;
    if (group.current) {
      group.current.rotation.y += delta * 0.04;
      group.current.rotation.x = Math.sin(performance.now() * 0.0002) * 0.08;
    }
    if (lines.current) {
      const m = lines.current.material as THREE.LineBasicMaterial;
      m.opacity = 0.04 + active * 0.35;
    }
    if (points.current) {
      const m = points.current.material as THREE.PointsMaterial;
      m.opacity = 0.55 + active * 0.45;
    }
  });

  return (
    <group ref={group}>
      <points ref={points} geometry={pointsGeom}>
        <pointsMaterial
          size={0.08}
          color="#e7c98a"
          transparent
          opacity={0.55}
          sizeAttenuation
          depthWrite={false}
          toneMapped={false}
        />
      </points>
      <lineSegments ref={lines} geometry={lineGeom}>
        <lineBasicMaterial
          color="#c9a24a"
          transparent
          opacity={0.2}
          depthWrite={false}
          toneMapped={false}
        />
      </lineSegments>
    </group>
  );
}
